// src/components/AuthorDetailsEditor.tsx
import React, { useState, useMemo } from 'react';
import { AuthorDetails } from '../types';
import { labelClass, inputClass, textareaClass, primaryButtonClass, actionButtonClass } from '../constants';
import ImageUpload from './ImageUpload'; // Import the new ImageUpload component
import { useAppContext } from '../contexts/AppContext';
import { 
    useViewEditMode, 
    ViewEditSection, 
    SaveCancelButtons, 
    DisplayField, 
    DisplayGrid 
} from '../utils/viewEditUtils';
import { useOptimizedSave } from '../hooks/useOptimizedSave';
import { useUpdateAuthorDetails } from '../../hooks/useBookStructureData';
import { useProfileImport } from '../hooks/useProfileImport';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { User, Download, Sparkles, CheckCircle, AlertCircle } from 'lucide-react';

interface AuthorDetailsEditorProps {
    authorDetails: Partial<AuthorDetails>;
    onAuthorDetailsChange: <K extends keyof AuthorDetails>(field: K, value: AuthorDetails[K]) => void;
    projectId?: string;
    // onSaveAuthorDetails: () => void; // Assuming save is handled by global project save
}

// Helper function to ensure all AuthorDetails fields have default values
const getAuthorDetailsWithDefaults = (authorDetails: Partial<AuthorDetails>): AuthorDetails => {
    return {
        authorName: authorDetails?.authorName || '',
        authorBio: authorDetails?.authorBio || '',
        authorWebsite: authorDetails?.authorWebsite || '',
        authorSocialMedia: authorDetails?.authorSocialMedia || '',
        authorContact: authorDetails?.authorContact || '',
        authorProfileImageBase64: authorDetails?.authorProfileImageBase64 || null,
        copyrightYear: authorDetails?.copyrightYear || '',
        isbn: authorDetails?.isbn || '',
        publisher: authorDetails?.publisher || '',
        edition: authorDetails?.edition || '',
        copyrightNotice: authorDetails?.copyrightNotice || '',
        // Professional Marketing Elements for Cover Generation
        publisherLogoBase64: authorDetails?.publisherLogoBase64 || null,
        frontCoverEndorsements: authorDetails?.frontCoverEndorsements || '',
        awardsAndBadges: authorDetails?.awardsAndBadges || [],
        // Back Cover Marketing Elements
        backCoverHookLine: authorDetails?.backCoverHookLine || '',
        backCoverEndorsements: authorDetails?.backCoverEndorsements || '',
        seriesDisplayInfo: authorDetails?.seriesDisplayInfo || '',
        qrCodeUrl: authorDetails?.qrCodeUrl || '',
        socialMediaHandles: authorDetails?.socialMediaHandles || '',
    };
};

const AuthorDetailsEditor: React.FC<AuthorDetailsEditorProps> = ({
    authorDetails,
    onAuthorDetailsChange,
    projectId,
}) => {
    // Ensure all fields have default values - memoized to prevent unnecessary re-renders
    const authorDetailsWithDefaults = useMemo(() => 
        getAuthorDetailsWithDefaults(authorDetails), 
        [authorDetails]
    );
    
    const appContext = useAppContext();
    const { activeEditSection, handleEditToggle, handleSaveAndExit, handleCancelEdit } = useViewEditMode();
    const { saveAuthorDetails } = useOptimizedSave();
    const updateAuthorDetails = useUpdateAuthorDetails();
    const { importFromProfile, enrichMissingFields, isImporting, profileData, profileLoading, profileError } = useProfileImport();
    
    // Profile import modal state
    const [showProfileImportModal, setShowProfileImportModal] = useState(false);
    const [importPreview, setImportPreview] = useState<AuthorDetails | null>(null);
    const [importStep, setImportStep] = useState<'preview' | 'enriching' | 'complete'>('preview');

    const handleSaveChanges = async () => {
        // Use projectId prop first, fallback to app context if not provided
        const effectiveProjectId = projectId || (appContext.dbProject as any)?.uuid || appContext.dbProject?.id?.toString() || '';

        if (!effectiveProjectId) {
            console.error('❌ [BOOK-STRUCTURE-STUDIO] No project ID available for author details save');
            return;
        }

        try {
            // Use new dedicated Book Structure endpoint for optimal performance
            await updateAuthorDetails.mutateAsync({
                projectId: effectiveProjectId,
                authorDetails: authorDetailsWithDefaults
            });

            handleSaveAndExit();
        } catch (error) {
            console.error('❌ [BOOK-STRUCTURE-STUDIO] AuthorDetailsEditor Save Changes failed:', error);

            // Fallback to old approach if new endpoint fails (during transition period)
            try {
                const { blueprint } = appContext.optimizedData || {};

                await saveAuthorDetails.mutateAsync({
                    projectId: effectiveProjectId,
                    authorDetails: authorDetailsWithDefaults,
                    setBlueprintSaving: blueprint?.setBlueprintSaving
                });

                handleSaveAndExit();
            } catch (fallbackError) {
                console.error('❌ [BOOK-STRUCTURE-STUDIO] Both save approaches failed:', fallbackError);
            }
        }
    };
    
    const fields: Array<{ key: keyof Omit<AuthorDetails, 'authorProfileImageBase64' | 'publisherLogoBase64' | 'awardsAndBadges' | 'frontCoverEndorsements' | 'backCoverHookLine' | 'backCoverEndorsements' | 'seriesDisplayInfo' | 'qrCodeUrl' | 'socialMediaHandles'>; label: string; placeholder: string; type?: 'input' | 'textarea', rows?: number }> = [
        { key: 'authorName', label: 'Author Name', placeholder: 'Enter author\'s full name or pen name.', type: 'input' },
        { key: 'authorBio', label: 'Author Bio', placeholder: 'Enter a brief biography. Markdown supported for formatting (e.g., paragraphs, italics).', type: 'textarea', rows: 5 },
        { key: 'authorWebsite', label: 'Author Website', placeholder: 'e.g., https://www.authorsite.com', type: 'input' },
        { key: 'authorSocialMedia', label: 'Social Media Links', placeholder: 'Enter social media links, one per line. Markdown supported for links (e.g., [Twitter](https://twitter.com/author)).', type: 'textarea', rows: 3 },
        { key: 'authorContact', label: 'Contact Information', placeholder: 'Enter contact details (e.g., email for professional inquiries). Markdown supported.', type: 'textarea', rows: 2 },
    ];
    
    const copyrightFields: Array<{ key: keyof Omit<AuthorDetails, 'authorProfileImageBase64' | 'publisherLogoBase64' | 'awardsAndBadges'>; label: string; placeholder: string; type?: 'input' | 'textarea', rows?: number }> = [
        { key: 'copyrightYear', label: 'Copyright Year', placeholder: 'e.g., 2024', type: 'input' },
        { key: 'isbn', label: 'ISBN', placeholder: 'e.g., 978-1234567890', type: 'input' },
        { key: 'publisher', label: 'Publisher', placeholder: 'e.g., Independent Publishing', type: 'input' },
        { key: 'edition', label: 'Edition', placeholder: 'e.g., First Edition', type: 'input' },
        { key: 'copyrightNotice', label: 'Copyright Notice', placeholder: 'e.g., All rights reserved. No part of this book may be reproduced...', type: 'textarea', rows: 3 },
    ];

    const coverMarketingFields: Array<{ key: keyof Omit<AuthorDetails, 'authorProfileImageBase64' | 'publisherLogoBase64' | 'awardsAndBadges'>; label: string; placeholder: string; type?: 'input' | 'textarea', rows?: number }> = [
        { key: 'frontCoverEndorsements', label: 'Front Cover Endorsements', placeholder: 'Brief endorsements suitable for front cover display. Use Markdown for formatting.', type: 'textarea', rows: 3 },
        { key: 'backCoverHookLine', label: 'Back Cover Hook Line', placeholder: 'A catchy one-liner for the back cover', type: 'input' },
        { key: 'backCoverEndorsements', label: 'Back Cover Endorsements', placeholder: 'Review quotes and praise from authors, media, etc. Use Markdown for formatting.', type: 'textarea', rows: 4 },
        { key: 'seriesDisplayInfo', label: 'Series Display Info', placeholder: 'How to display series info on covers (e.g., "Book 1 of the Epic Fantasy Series")', type: 'input' },
        { key: 'qrCodeUrl', label: 'QR Code URL', placeholder: 'URL for QR code generation (e.g., author website, book page)', type: 'input' },
        { key: 'socialMediaHandles', label: 'Social Media Handles', placeholder: 'Social handles for back cover display (e.g., @author_twitter, @author_instagram)', type: 'textarea', rows: 2 },
    ];

    const handleImageUpload = (base64Image: string | null) => {
        onAuthorDetailsChange('authorProfileImageBase64', base64Image);
    };

    const handlePublisherLogoUpload = (base64Image: string | null) => {
        onAuthorDetailsChange('publisherLogoBase64', base64Image);
    };

    const handleAwardsChange = (awards: string[]) => {
        onAuthorDetailsChange('awardsAndBadges', awards);
    };

    // Profile import handlers
    const handleImportProfile = async () => {
        try {
            const importedData = await importFromProfile();
            setImportPreview(importedData);
            setImportStep('preview');
            setShowProfileImportModal(true);
        } catch (error) {
            console.error('❌ [BOOK-STRUCTURE-STUDIO] AuthorDetailsEditor Import Profile failed:', error);
            // Show error state in modal
            setShowProfileImportModal(true);
        }
    };

    const handleApplyImport = (withEnrichment: boolean = false) => {
        if (!importPreview) return;

        console.log('🔍 [BOOK-STRUCTURE-STUDIO] AuthorDetailsEditor applying import:', {
            withEnrichment,
            importPreview,
            currentAuthorDetails: authorDetails
        });

        if (withEnrichment) {
            setImportStep('enriching');
            // Apply enrichment with AI
            enrichMissingFields(importPreview, appContext.projectState)
                .then((enrichedData) => {

                    // Apply the enriched data to the form
                    Object.entries(enrichedData).forEach(([key, value]) => {
                        onAuthorDetailsChange(key as keyof AuthorDetails, value);
                    });
                    setImportStep('complete');
                    setTimeout(() => setShowProfileImportModal(false), 1500);
                })
                .catch((error) => {
                    console.error('❌ [BOOK-STRUCTURE-STUDIO] AuthorDetailsEditor AI enrichment failed:', error);
                    // Fallback: apply basic import without enrichment
                    Object.entries(importPreview).forEach(([key, value]) => {
                        onAuthorDetailsChange(key as keyof AuthorDetails, value);
                    });
                    setShowProfileImportModal(false);
                });
        } else {
            // Apply direct import without enrichment
            Object.entries(importPreview).forEach(([key, value]) => {
                onAuthorDetailsChange(key as keyof AuthorDetails, value);
            });

            setShowProfileImportModal(false);
        }
    };

    const handleCloseImportModal = () => {
        setShowProfileImportModal(false);
        setImportPreview(null);
        setImportStep('preview');
    };

    const renderAuthorEditForm = () => (
        <div className="space-y-4">
            {fields.map(({ key, label, placeholder, type = 'textarea', rows = 3 }) => (
                <div key={String(key)}>
                    <label htmlFor={`ad-${String(key)}`} className={`${labelClass} mb-1.5`}>{label}:</label>
                    {type === 'textarea' ? (
                         <textarea
                            id={`ad-${String(key)}`}
                            value={String(authorDetailsWithDefaults[key as keyof AuthorDetails] || '')}
                            onChange={(e) => onAuthorDetailsChange(key as (string & keyof AuthorDetails), e.target.value as any)}
                            placeholder={placeholder}
                            className={`${textareaClass} min-h-[${rows * 1.5}rem]`}
                            rows={rows}
                        />
                    ) : (
                        <input
                            id={`ad-${String(key)}`}
                            type="text"
                            value={String(authorDetailsWithDefaults[key as keyof AuthorDetails] || '')}
                            onChange={(e) => onAuthorDetailsChange(key as (string & keyof AuthorDetails), e.target.value as any)}
                            placeholder={placeholder}
                            className={inputClass}
                        />
                    )}
                     <p className="text-xs text-gray-500 mt-1">
                        {key === 'authorBio' || key === 'authorSocialMedia' || key === 'authorContact' ? 'Supports Markdown for formatting.' : ''}
                    </p>
                </div>
            ))}

            <div>
                <ImageUpload
                    label="Author Profile Image"
                    currentImage={authorDetailsWithDefaults.authorProfileImageBase64}
                    onImageUpload={handleImageUpload}
                    recommendedSize="e.g., 300x300px"
                />
            </div>
        </div>
    );

    const renderCopyrightEditForm = () => (
        <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">Information used for copyright pages and book metadata.</p>
            {copyrightFields.map(({ key, label, placeholder, type = 'input', rows = 3 }) => (
                <div key={String(key)}>
                    <label htmlFor={`cp-${String(key)}`} className={`${labelClass} mb-1.5`}>{label}:</label>
                    {type === 'textarea' ? (
                         <textarea
                            id={`cp-${String(key)}`}
                            value={String(authorDetailsWithDefaults[key as keyof AuthorDetails] || '')}
                            onChange={(e) => onAuthorDetailsChange(key as (string & keyof AuthorDetails), e.target.value as any)}
                            placeholder={placeholder}
                            className={`${textareaClass} min-h-[${rows * 1.5}rem]`}
                            rows={rows}
                        />
                    ) : (
                        <input
                            id={`cp-${String(key)}`}
                            type="text"
                            value={String(authorDetailsWithDefaults[key as keyof AuthorDetails] || '')}
                            onChange={(e) => onAuthorDetailsChange(key as (string & keyof AuthorDetails), e.target.value as any)}
                            placeholder={placeholder}
                            className={inputClass}
                        />
                    )}
                    {key === 'isbn' && (
                        <p className="text-xs text-gray-500 mt-1">
                            International Standard Book Number - get one from your country's ISBN agency.
                        </p>
                    )}
                </div>
            ))}
        </div>
    );

    const renderCoverMarketingEditForm = () => (
        <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">Marketing elements that will be used for professional cover generation.</p>

            {/* Publisher Logo Upload */}
            <div>
                <ImageUpload
                    label="Publisher Logo"
                    currentImage={authorDetailsWithDefaults.publisherLogoBase64 ?? null}
                    onImageUpload={handlePublisherLogoUpload}
                    recommendedSize="e.g., 300x100px"
                />
                <p className="text-xs text-gray-500 mt-1">
                    Upload your publisher or imprint logo for cover display.
                </p>
            </div>

            {/* Awards and Badges */}
            <div>
                <label className={`${labelClass} mb-1.5`}>Awards & Badges:</label>
                <div className="space-y-2">
                    {authorDetailsWithDefaults.awardsAndBadges?.map((award, index) => (
                        <div key={index} className="flex gap-2">
                            <input
                                type="text"
                                value={award}
                                onChange={(e) => {
                                    const newAwards = [...(authorDetailsWithDefaults.awardsAndBadges || [])];
                                    newAwards[index] = e.target.value;
                                    handleAwardsChange(newAwards);
                                }}
                                placeholder="e.g., New York Times Bestseller"
                                className={`${inputClass} flex-1`}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    const newAwards = authorDetailsWithDefaults.awardsAndBadges?.filter((_, i) => i !== index) || [];
                                    handleAwardsChange(newAwards);
                                }}
                                className="px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => {
                            const newAwards = [...(authorDetailsWithDefaults.awardsAndBadges || []), ''];
                            handleAwardsChange(newAwards);
                        }}
                        className="text-sm text-blue-600 hover:bg-blue-50 px-2 py-1 rounded"
                    >
                        + Add Award/Badge
                    </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                    Add awards, achievements, or badges that should appear on covers.
                </p>
            </div>

            {/* Regular text fields */}
            {coverMarketingFields.map(({ key, label, placeholder, type = 'textarea', rows = 3 }) => (
                <div key={String(key)}>
                    <label htmlFor={`cm-${String(key)}`} className={`${labelClass} mb-1.5`}>{label}:</label>
                    {type === 'textarea' ? (
                         <textarea
                            id={`cm-${String(key)}`}
                            value={String(authorDetailsWithDefaults[key as keyof AuthorDetails] || '')}
                            onChange={(e) => onAuthorDetailsChange(key as (string & keyof AuthorDetails), e.target.value as any)}
                            placeholder={placeholder}
                            className={`${textareaClass} min-h-[${rows * 1.5}rem]`}
                            rows={rows}
                        />
                    ) : (
                        <input
                            id={`cm-${String(key)}`}
                            type="text"
                            value={String(authorDetailsWithDefaults[key as keyof AuthorDetails] || '')}
                            onChange={(e) => onAuthorDetailsChange(key as (string & keyof AuthorDetails), e.target.value as any)}
                            placeholder={placeholder}
                            className={inputClass}
                        />
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                        {(key === 'frontCoverEndorsements' || key === 'backCoverEndorsements') ? 'Supports Markdown for formatting.' : ''}
                    </p>
                </div>
            ))}
        </div>
    );

    const getDisplayValue = (value: AuthorDetails[keyof AuthorDetails]): string | undefined => {
        if (value == null) return undefined;
        if (Array.isArray(value)) return value.join(', ');
        return String(value);
    };

    const renderAuthorDisplayView = () => {
        // Check if any author details are filled
        const hasAuthorData = fields.some(({ key }) => {
            const value = authorDetailsWithDefaults[key as keyof AuthorDetails];
            return value && String(value).trim() !== '';
        }) || authorDetailsWithDefaults.authorProfileImageBase64;

        if (!hasAuthorData) {
            return (
                <div className="text-center py-8">
                    <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 mb-2">No Author Information Added</h3>
                    <p className="text-gray-500 mb-4">
                        Click "Edit" to add author information that can be used to generate book elements like "About the Author" sections.
                    </p>
                </div>
            );
        }

        return (
            <div className="space-y-3">
                <DisplayGrid>
                    {fields.map(({ key, label, type }) => {
                        const rawValue = authorDetailsWithDefaults[key as keyof AuthorDetails];
                        const value = getDisplayValue(rawValue);
                        if (!value || value.trim() === '') return null;

                        return (
                            <DisplayField
                                key={String(key)}
                                label={label}
                                value={value}
                                className={type === 'textarea' ? 'md:col-span-2' : ''}
                                background={true}
                            />
                        );
                    })}
                </DisplayGrid>
                {authorDetailsWithDefaults.authorProfileImageBase64 && (
                    <div className="mt-4">
                        <h5 className="font-medium text-gray-700 mb-2">Author Profile Image</h5>
                        <img 
                            src={authorDetailsWithDefaults.authorProfileImageBase64} 
                            alt="Author Profile" 
                            className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                        />
                    </div>
                )}
            </div>
        );
    };

    const renderCopyrightDisplayView = () => {
        const hasCopyrightData = copyrightFields.some(({ key }) => {
            const value = authorDetailsWithDefaults[key as keyof AuthorDetails];
            return value && String(value).trim() !== '';
        });

        if (!hasCopyrightData) {
            return (
                <div className="text-center py-8">
                    <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 mb-2">No Copyright & Publishing Info Added</h3>
                    <p className="text-gray-500 mb-4">
                        Click "Edit" to add publishing details that can be used for copyright pages and book metadata.
                    </p>
                </div>
            );
        }

        return (
            <div className="space-y-3">
                <DisplayGrid>
                    {copyrightFields.map(({ key, label, type }) => {
                        const rawValue = authorDetailsWithDefaults[key as keyof AuthorDetails];
                        const value = getDisplayValue(rawValue);
                        if (!value || value.trim() === '') return null;

                        return (
                            <DisplayField
                                key={String(key)}
                                label={label}
                                value={value}
                                className={type === 'textarea' ? 'md:col-span-2' : ''}
                                background={true}
                            />
                        );
                    })}
                </DisplayGrid>
            </div>
        );
    };

    const renderCoverMarketingDisplayView = () => {
        const hasCoverMarketingData =
            authorDetailsWithDefaults.publisherLogoBase64 ||
            (authorDetailsWithDefaults.awardsAndBadges && authorDetailsWithDefaults.awardsAndBadges.length > 0) ||
            coverMarketingFields.some(({ key }) => {
                const value = authorDetailsWithDefaults[key as keyof AuthorDetails];
                return value && String(value).trim() !== '';
            });

        if (!hasCoverMarketingData) {
            return (
                <div className="text-center py-8">
                    <Sparkles className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 mb-2">No Cover Marketing Elements Added</h3>
                    <p className="text-gray-500 mb-4">
                        Click "Edit" to add marketing elements that will be used for professional cover generation.
                    </p>
                </div>
            );
        }

        return (
            <div className="space-y-3">
                {/* Publisher Logo */}
                {authorDetailsWithDefaults.publisherLogoBase64 && (
                    <div className="mb-4">
                        <h5 className="font-medium text-gray-700 mb-2">Publisher Logo</h5>
                        <img
                            src={authorDetailsWithDefaults.publisherLogoBase64}
                            alt="Publisher Logo"
                            className="h-16 w-auto object-contain rounded border border-gray-300"
                        />
                    </div>
                )}

                {/* Awards and Badges */}
                {authorDetailsWithDefaults.awardsAndBadges && authorDetailsWithDefaults.awardsAndBadges.length > 0 && (
                    <div className="mb-4">
                        <h5 className="font-medium text-gray-700 mb-2">Awards & Badges</h5>
                        <div className="flex flex-wrap gap-2">
                            {authorDetailsWithDefaults.awardsAndBadges.map((award, index) => (
                                award.trim() && (
                                    <span key={index} className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                                        {award}
                                    </span>
                                )
                            ))}
                        </div>
                    </div>
                )}

                {/* Regular fields */}
                <DisplayGrid>
                    {coverMarketingFields.map(({ key, label, type }) => {
                        const rawValue = authorDetailsWithDefaults[key as keyof AuthorDetails];
                        const value = getDisplayValue(rawValue);
                        if (!value || value.trim() === '') return null;

                        return (
                            <DisplayField
                                key={String(key)}
                                label={label}
                                value={value}
                                className={type === 'textarea' ? 'md:col-span-2' : ''}
                                background={true}
                            />
                        );
                    })}
                </DisplayGrid>
            </div>
        );
    };

    return (
        <div className="space-y-6 p-1 h-full overflow-y-auto">
            <h2 className="text-2xl font-semibold text-gray-800 border-b pb-3 mb-6">Author & Publishing Info</h2>
            <p className="text-sm text-gray-600 mb-6">
                Provide information about the author and publishing details. This can be used by the AI to help generate elements like "About the Author" sections, copyright pages, and tailored dedications.
            </p>
            
            <ViewEditSection
                title="Author Information"
                subtitle="Personal and professional details"
                isActive={activeEditSection === 'author'}
                onEditToggle={() => handleEditToggle('author')}
                headerActions={
                    activeEditSection === 'author' ? (
                        <Button
                            onClick={handleImportProfile}
                            disabled={profileLoading || isImporting}
                            className={`${actionButtonClass} flex items-center gap-2 text-sm`}
                            size="sm"
                        >
                            <User className="h-4 w-4" />
                            {profileLoading ? 'Loading...' : 'Import from Profile'}
                        </Button>
                    ) : null
                }
            >
                {activeEditSection === 'author' ? (
                    <>
                        {renderAuthorEditForm()}
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </>
                ) : (
                    renderAuthorDisplayView()
                )}
            </ViewEditSection>

            <ViewEditSection
                title="Copyright & Publishing Information"
                subtitle="Information used for copyright pages and book metadata"
                isActive={activeEditSection === 'copyright'}
                onEditToggle={() => handleEditToggle('copyright')}
            >
                {activeEditSection === 'copyright' ? (
                    <>
                        {renderCopyrightEditForm()}
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </>
                ) : (
                    renderCopyrightDisplayView()
                )}
            </ViewEditSection>

            <ViewEditSection
                title="Cover Marketing Elements"
                subtitle="Marketing content for professional cover generation"
                isActive={activeEditSection === 'coverMarketing'}
                onEditToggle={() => handleEditToggle('coverMarketing')}
            >
                {activeEditSection === 'coverMarketing' ? (
                    <>
                        {renderCoverMarketingEditForm()}
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </>
                ) : (
                    renderCoverMarketingDisplayView()
                )}
            </ViewEditSection>

            <p className="text-xs text-gray-500 mt-6">
                Changes to author details are saved as part of the overall project. Remember to use "Save Project" to persist them.
                <span className="font-semibold">Note:</span> You must save the project to enable navigation to other pages.
            </p>

            {/* Profile Import Modal */}
            <Dialog open={showProfileImportModal} onOpenChange={setShowProfileImportModal}>
                <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <User className="h-5 w-5" />
                            Import Author Profile
                        </DialogTitle>
                        <DialogDescription>
                            {importStep === 'preview' && 'Review and import your profile information'}
                            {importStep === 'enriching' && 'AI is enriching your profile with additional details...'}
                            {importStep === 'complete' && 'Profile imported successfully!'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Error State */}
                        {profileError && (
                            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-md">
                                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                                <div>
                                    <p className="text-red-800 font-medium">Failed to load profile</p>
                                    <p className="text-red-600 text-sm">{profileError}</p>
                                </div>
                            </div>
                        )}

                        {/* Loading State */}
                        {importStep === 'enriching' && (
                            <div className="flex items-center gap-3 p-6 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="animate-spin">
                                    <Sparkles className="h-6 w-6 text-blue-500" />
                                </div>
                                <div>
                                    <p className="font-medium text-blue-800">AI Enhancement in Progress</p>
                                    <p className="text-blue-600 text-sm">Filling in missing fields and optimizing content...</p>
                                </div>
                            </div>
                        )}

                        {/* Success State */}
                        {importStep === 'complete' && (
                            <div className="flex items-center gap-3 p-6 bg-green-50 border border-green-200 rounded-md">
                                <CheckCircle className="h-6 w-6 text-green-500" />
                                <div>
                                    <p className="font-medium text-green-800">Import Complete!</p>
                                    <p className="text-green-600 text-sm">Your author profile has been successfully imported.</p>
                                </div>
                            </div>
                        )}

                        {/* Preview State */}
                        {importStep === 'preview' && importPreview && (
                            <>
                                <div className="space-y-3">
                                    <h4 className="font-medium text-gray-900">Profile Preview</h4>
                                    <div className="grid grid-cols-1 gap-3 p-4 bg-gray-50 rounded-md max-h-64 overflow-y-auto">
                                        {Object.entries(importPreview).map(([key, value]) => {
                                            if (key === 'authorProfileImageBase64' || !value) return null;
                                            const fieldInfo = fields.find(f => f.key === key);
                                            return (
                                                <div key={key} className="grid grid-cols-3 gap-2 text-sm">
                                                    <span className="font-medium text-gray-700">
                                                        {fieldInfo?.label || key}:
                                                    </span>
                                                    <span className="col-span-2 text-gray-600">
                                                        {typeof value === 'string' && value.length > 50 
                                                            ? `${value.substring(0, 50)}...` 
                                                            : String(value)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                        {Object.values(importPreview).every(v => !v || (typeof v === 'string' && v.trim() === '')) && (
                                            <p className="text-gray-500 text-sm">No profile data found to import.</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-4 border-t">
                                    <Button
                                        onClick={() => handleApplyImport(false)}
                                        className="flex-1"
                                        variant="outline"
                                    >
                                        <Download className="h-4 w-4 mr-2" />
                                        Import As-Is
                                    </Button>
                                    <Button
                                        onClick={() => handleApplyImport(true)}
                                        className="flex-1"
                                        disabled={isImporting}
                                    >
                                        <Sparkles className="h-4 w-4 mr-2" />
                                        Import + AI Fill Gaps
                                    </Button>
                                </div>
                            </>
                        )}

                        {/* No preview available */}
                        {importStep === 'preview' && !importPreview && !profileError && (
                            <div className="text-center py-6">
                                <User className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                                <p className="text-gray-600">No profile data available to import.</p>
                                <p className="text-gray-500 text-sm mt-1">
                                    Please complete your profile in the Profile page first.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Modal Footer */}
                    {importStep === 'preview' && (
                        <div className="flex justify-end pt-4 border-t">
                            <Button onClick={handleCloseImportModal} variant="ghost">
                                Cancel
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AuthorDetailsEditor;